/**
 * LAWD Voice Alert System
 * Uses Web Speech API for driver fatigue warnings
 * 
 * Features:
 * - Mute toggle (localStorage)
 * - Night mode auto volume (22:00-06:00)
 * - Business layer warning: "Time for a rest"
 * - LAW layer warning (FRHV only): "You must pull over and rest within 10 minutes"
 */

const STORAGE_KEY = 'lawd_voice_muted';
const NIGHT_START_HOUR = 22;
const NIGHT_END_HOUR = 6;

// Check if current time is in night hours (22:00-06:00)
const isNightTime = () => {
  const hour = new Date().getHours();
  return hour >= NIGHT_START_HOUR || hour < NIGHT_END_HOUR;
};

// Get mute setting from localStorage
export const isVoiceMuted = () => {
  return localStorage.getItem(STORAGE_KEY) === 'true';
};

// Set mute setting
export const setVoiceMuted = (muted) => {
  localStorage.setItem(STORAGE_KEY, muted ? 'true' : 'false');
};

// Toggle mute setting
export const toggleVoiceMute = () => {
  const current = isVoiceMuted();
  setVoiceMuted(!current);
  return !current;
};

// Speak a message using Web Speech API
export const speak = (message, options = {}) => {
  // Check if muted
  if (isVoiceMuted()) {
    return false;
  }

  // Check if speech synthesis is available
  if (!window.speechSynthesis) {
    return false;
  }

  // Cancel any ongoing speech
  window.speechSynthesis.cancel();

  const utterance = new SpeechSynthesisUtterance(message);
  
  // Set volume based on night mode
  const nightMode = isNightTime();
  utterance.volume = nightMode ? 0.4 : 1.0; // 40% volume at night
  
  // Set other properties
  utterance.rate = options.rate || 0.9; // Slightly slower for clarity
  utterance.pitch = options.pitch || 1.0;
  utterance.lang = 'en-AU'; // Australian English
  
  // Try to use an Australian voice if available
  const voices = window.speechSynthesis.getVoices();
  const ausVoice = voices.find(v => v.lang === 'en-AU') || 
                   voices.find(v => v.lang.startsWith('en-'));
  if (ausVoice) {
    utterance.voice = ausVoice;
  }

  window.speechSynthesis.speak(utterance);
  return true;
};

// Pre-defined alert messages
export const VOICE_MESSAGES = {
  BUSINESS_WARNING: "Time for a rest",
  LAW_WARNING: "You must pull over and rest within 10 minutes"
};

// Business layer warning (all regimes)
export const speakBusinessWarning = () => {
  return speak(VOICE_MESSAGES.BUSINESS_WARNING);
};

// LAW layer warning (FRHV only)
export const speakLawWarning = () => {
  return speak(VOICE_MESSAGES.LAW_WARNING);
};

// Initialize voices (needed for some browsers)
export const initVoices = () => {
  if (window.speechSynthesis) {
    // Some browsers need this to load voices
    window.speechSynthesis.getVoices();
    
    // Chrome requires this event listener
    if (window.speechSynthesis.onvoiceschanged !== undefined) {
      window.speechSynthesis.onvoiceschanged = () => {
        window.speechSynthesis.getVoices();
      };
    }
  }
};

export default {
  speak,
  speakBusinessWarning,
  speakLawWarning,
  isVoiceMuted,
  setVoiceMuted,
  toggleVoiceMute,
  initVoices,
  VOICE_MESSAGES
};
